import React, { useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  Box,
  Button,
  Card,
  Chip,
  Skeleton,
  Stack,
  Typography,
} from "@mui/material";
import {
  Email as EmailIcon,
  Phone as PhoneIcon,
} from "@mui/icons-material";
import { setSnackbar } from "modules/snackbar/api/snackbar";
import { formatMoney } from "setup/helpers";
import { DIALOGS } from "../hooks/useDialogManager";

const FinancialSummary = React.memo(function FinancialSummary({ openDialog }) {
  const dispatch = useDispatch();
  const account = useSelector((state) => state.account);
  const payments = useSelector((state) => state.payments);
  const bills = useSelector((state) => state.bills);
  const accountDisconnects = useSelector((state) => state.accountDisconnects);

  const mainLoading =
    account.isLoading ||
    payments.latestPaymentIsLoading ||
    bills.latestBillIsLoading;
  const disconnectLoading =
    accountDisconnects.isLoading ||
    mainLoading; /* show skeleton until main data ready (avoids N/A before fetch starts) */

  const disconnectInfo = useMemo(() => {
    const hasActive =
      !accountDisconnects.isLoading &&
      accountDisconnects.data.accountDisconnectsList.items &&
      accountDisconnects.data.accountDisconnectsList.items[0] &&
      !accountDisconnects.data.accountDisconnectsList.items[0].completed;

    if (!hasActive) return null;

    const newestDisc = accountDisconnects.data.accountDisconnectsList.items[0];
    let currentStatus = "None Found";
    let statusColor = "text.primary";
    let disconnectDate = null;

    if (newestDisc) {
      disconnectDate = newestDisc.disconnectDate;
      if (newestDisc.letterDisconnectId != null) {
        currentStatus = "Disconnect Posted";
        statusColor = "error.main";
      } else if (newestDisc.letterFinalNoticeId != null) {
        currentStatus = "Final Notice";
        statusColor = "warning.main";
      } else if (newestDisc.letter10DayId != null) {
        currentStatus = "10 Day Notice";
        statusColor = "warning.main";
      } else if (newestDisc.letter14DayId != null) {
        currentStatus = "14 Day Notice";
        statusColor = "warning.main";
      }
    }

    return { currentStatus, statusColor, disconnectDate };
  }, [accountDisconnects]);

  const contactButtonSx = {
    minWidth: "auto",
    px: 2,
    py: 0.5,
    fontSize: "0.75rem",
    textTransform: "none",
    fontWeight: 500,
  };

  return (
    <Box
      display="flex"
      justifyContent={{ xs: "flex-start", md: "flex-end" }}
      alignItems="flex-start"
    >
      <Stack spacing={2} alignItems="flex-end">
        <Box display="flex" gap={1} sx={{ mb: 1 }}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<EmailIcon />}
            onClick={() => openDialog(DIALOGS.CONTACT_ACCOUNT_HOLDER)}
            sx={contactButtonSx}
          >
            Send Email
          </Button>
          <Button
            variant="outlined"
            size="small"
            startIcon={<PhoneIcon />}
            onClick={() => {
              const phoneNumber =
                account.data.account?.phone || account.data.account?.phone1;
              if (phoneNumber) {
                window.open(`tel:${phoneNumber}`, "_self");
              }
            }}
            disabled={
              !account.data.account?.phone && !account.data.account?.phone1
            }
            sx={contactButtonSx}
          >
            Call
          </Button>
        </Box>

        <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
          {/* Balance */}
          <Box sx={{ flex: "1 1 0", minWidth: 0 }}>
            <Card variant="outlined" sx={{ p: 3, textAlign: "center", height: 140, boxSizing: "border-box" }}>
              {mainLoading ? (
                <>
                  <Skeleton variant="text" width={60} height={14} sx={{ mx: "auto", mb: 1 }} />
                  <Skeleton variant="text" width={100} height={24} sx={{ mx: "auto", mb: 0.5 }} />
                  <Skeleton variant="text" width={50} height={14} sx={{ mx: "auto" }} />
                </>
              ) : (
                <>
                  <Typography variant="body2" color="text.secondary" sx={{ display: "block", mb: 1, fontWeight: 500 }}>
                    Balance
                  </Typography>
                  <Typography variant="h4" fontWeight="bold" color="text.primary" sx={{ mb: 0.5 }}>
                    {account.data.ongoingAccountBalance?.id
                      ? formatMoney(account.data.ongoingAccountBalance.balance)
                      : "$0.00"}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Current
                  </Typography>
                </>
              )}
            </Card>
          </Box>

          {/* Payment Due */}
          <Box sx={{ flex: "1 1 0", minWidth: 0 }}>
            <Card variant="outlined" sx={{ p: 3, textAlign: "center", height: 140, boxSizing: "border-box" }}>
              {mainLoading ? (
                <>
                  <Skeleton variant="text" width={80} height={14} sx={{ mx: "auto", mb: 1 }} />
                  <Skeleton variant="text" width={100} height={24} sx={{ mx: "auto", mb: 0.5 }} />
                  <Skeleton variant="text" width={60} height={14} sx={{ mx: "auto" }} />
                </>
              ) : (
                <>
                  <Typography variant="body2" color="text.secondary" sx={{ display: "block", mb: 1, fontWeight: 500 }}>
                    Payment Due
                  </Typography>
                  <Typography
                    variant="h4"
                    fontWeight="bold"
                    color={
                      account.data.ongoingAccountBalance?.pastDue > 0
                        ? "error.main"
                        : "text.primary"
                    }
                    sx={{ mb: 0.5 }}
                  >
                    {account.data.ongoingAccountBalance?.id
                      ? formatMoney(account.data.ongoingAccountBalance.pastDue)
                      : "$0.00"}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Past Due
                  </Typography>
                </>
              )}
            </Card>
          </Box>

          {/* Last Payment */}
          <Box sx={{ flex: "1 1 0", minWidth: 0 }}>
            <Card variant="outlined" sx={{ p: 3, textAlign: "center", height: 140, boxSizing: "border-box" }}>
              {mainLoading ? (
                <>
                  <Skeleton variant="text" width={90} height={14} sx={{ mx: "auto", mb: 1 }} />
                  <Skeleton variant="text" width={100} height={24} sx={{ mx: "auto", mb: 0.5 }} />
                  <Skeleton variant="text" width={70} height={14} sx={{ mx: "auto" }} />
                </>
              ) : (
                <>
                  <Typography variant="body2" color="text.secondary" sx={{ display: "block", mb: 1, fontWeight: 500 }}>
                    Last Payment
                  </Typography>
                  <Typography variant="h4" fontWeight="bold" color="text.primary" sx={{ mb: 0.5 }}>
                    {payments.latestPayment
                      ? formatMoney(
                          payments.latestPayment.paymentAmount +
                            (payments.latestPayment.feeAmount || 0)
                        )
                      : "$0.00"}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {payments.latestPayment
                      ? new Date(payments.latestPayment.paymentDate).toLocaleDateString("en-US", {
                          month: "short",
                          day: "numeric",
                        })
                      : "No Payment"}
                  </Typography>
                </>
              )}
            </Card>
          </Box>

          {/* Last Bill */}
          <Box sx={{ flex: "1 1 0", minWidth: 0 }}>
            <Card
              variant="outlined"
              sx={{
                p: 3,
                textAlign: "center",
                height: 140,
                boxSizing: "border-box",
                cursor: "pointer",
                "&:hover": { backgroundColor: "action.hover", boxShadow: 2 },
              }}
              onClick={() => {
                if (bills.latestBill?.url) {
                  window.open(bills.latestBill.url, "_blank", "noopener,noreferrer");
                }
              }}
            >
              {mainLoading ? (
                <>
                  <Skeleton variant="text" width={60} height={14} sx={{ mx: "auto", mb: 1 }} />
                  <Skeleton variant="text" width={100} height={24} sx={{ mx: "auto", mb: 0.5 }} />
                  <Skeleton variant="text" width={70} height={14} sx={{ mx: "auto" }} />
                </>
              ) : (
                <>
                  <Typography variant="body2" color="text.secondary" sx={{ display: "block", mb: 1, fontWeight: 500 }}>
                    Last Bill
                  </Typography>
                  <Typography variant="h4" fontWeight="bold" color="text.primary" sx={{ mb: 0.5 }}>
                    {bills.latestBill
                      ? formatMoney(bills.latestBill.newCharges)
                      : "$0.00"}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {bills.latestBill
                      ? new Date(bills.latestBill.billDate).toLocaleDateString("en-US", {
                          month: "short",
                          day: "numeric",
                        })
                      : "No Bill"}
                  </Typography>
                </>
              )}
            </Card>
          </Box>

          {/* Disconnect Status */}
          <Box sx={{ flex: "1 1 0", minWidth: 0 }}>
            <Card
              variant="outlined"
              sx={{
                p: 3,
                textAlign: "center",
                height: 140,
                boxSizing: "border-box",
                borderColor: disconnectInfo ? "warning.main" : undefined,
              }}
            >
              {disconnectLoading ? (
                <>
                  <Skeleton variant="text" width={110} height={14} sx={{ mx: "auto", mb: 1 }} />
                  <Skeleton variant="text" width={90} height={18} sx={{ mx: "auto", mb: 0.5 }} />
                  <Skeleton variant="text" width={50} height={14} sx={{ mx: "auto" }} />
                </>
              ) : (
                <>
                  <Typography variant="body2" color="text.secondary" sx={{ display: "block", mb: 1, fontWeight: 500 }}>
                    Disconnect Status
                  </Typography>
                  <Typography
                    variant="body1"
                    fontWeight="bold"
                    color={disconnectInfo?.statusColor || "text.primary"}
                    sx={{ mb: 0.5, fontSize: "0.875rem", lineHeight: 1.4 }}
                  >
                    {disconnectInfo?.currentStatus || "N/A"}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {disconnectInfo?.disconnectDate
                      ? new Date(disconnectInfo.disconnectDate).toLocaleDateString("en-US", {
                          month: "short",
                          day: "numeric",
                        })
                      : ""}
                  </Typography>
                </>
              )}
            </Card>
          </Box>
        </Box>
      </Stack>
    </Box>
  );
});

export default FinancialSummary;
